"use client"

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  type CollectionReference,
} from "firebase/firestore"
import { db as firestore } from "./config"
import type { User, Comment } from "@/lib/types"

// Get comments collection reference
function getCommentsCollection() {
  if (!firestore) {
    throw new Error("Firebase is not initialized")
  }
  return collection(firestore, "comments")
}

export async function getCommentsByPostId(postId: string): Promise<Comment[]> {
  try {
    const q = query(
      getCommentsCollection(),
      where("postId", "==", postId),
      orderBy("createdAt", "desc") // Just order by creation time to get all comments
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        postId: data.postId,
        content: data.content,
        author: {
          uid: data.author.uid,
          displayName: data.author.displayName,
          username: data.author.username
        },
        parentId: data.parentId || undefined,
        number: data.number,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      }
    })
  } catch (error) {
    console.error("Error getting comments:", error)
    return []
  }
}

export async function createClientComment(
  postId: string,
  content: string,
  user: User,
  username?: string,
  parentId?: string
): Promise<string> {
  if (!firestore) {
    throw new Error("Firebase is not initialized")
  }

  // Create query to get the latest comment number

  const q = query(
    getCommentsCollection(),
    where("postId", "==", postId),
    where("parentId", "==", null),
    orderBy("createdAt", "desc")
  )
  const snapshot = await getDocs(q)
  
  const comment = {
    postId,
    content,
    author: {
      uid: user.uid,
      displayName: user.displayName,
      username: username || null
    },
    parentId: parentId || null,
    ...(parentId ? {} : { number: snapshot.size + 1 }), // Only include number for top-level comments
    createdAt: new Date(), // Use actual date instead of serverTimestamp for immediate display
    updatedAt: new Date()
  }

  const docRef = await addDoc(getCommentsCollection(), comment)
  return docRef.id
}

export async function deleteClientComment(commentId: string, userId: string): Promise<void> {
  if (!firestore) {
    throw new Error("Firebase is not initialized")
  }

  const commentRef = doc(firestore, "comments", commentId)
  const comment = await getDoc(commentRef)
  
  if (!comment.exists()) {
    throw new Error("Comment not found")
  }
  
  const commentData = comment.data()
  if (commentData?.author.uid !== userId) {
    throw new Error("Unauthorized to delete this comment")
  }
  
  await deleteDoc(commentRef)
}
